/**
 * Derives a human-readable identity provider label from the Auth0 `sub` claim.
 *
 * `sub` is formatted as `<strategy>|<id>` for database/social connections
 * (e.g. `auth0|abc123`, `google-oauth2|123`), or `<strategy>|<connection>|<id>`
 * for enterprise/federated connections (e.g. `oidc|okta-iris-northamerica|abc`,
 * `samlp|okta-iris-northamerica|abc`, `waad|contoso.com|abc`).
 *
 * This avoids hardcoding a single connection name in the UI — multiple
 * connections (Okta, Google, username/password, etc.) may be enabled on the
 * Auth0 tenant, and this derives the right label per signed-in user.
 */

const STRATEGY_LABELS: Record<string, string> = {
  auth0: 'Username & Password',
  'google-oauth2': 'Google',
  google: 'Google',
  windowslive: 'Microsoft Account',
  github: 'GitHub',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  apple: 'Apple',
  sms: 'SMS (passwordless)',
  email: 'Email (passwordless)',
};

export interface IdentityProviderInfo {
  /** Human-readable label, e.g. "Okta Iris Northamerica (okta-iris-northamerica)" or "Google". */
  label: string;
  /** Raw connection or strategy name from the `sub` claim, e.g. "okta-iris-northamerica". */
  connection?: string;
  /** True for enterprise/federated connections (Okta, Azure AD, SAML, OIDC, etc.). */
  isEnterprise: boolean;
}

function prettify(name: string): string {
  return name
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function getIdentityProviderInfo(sub?: string): IdentityProviderInfo {
  if (!sub) return { label: 'Unknown', isEnterprise: false };

  const parts = sub.split('|');
  if (parts.length >= 3) {
    const connection = parts[1];
    return { label: `${prettify(connection)} (${connection})`, connection, isEnterprise: true };
  }

  const strategy = parts[0];
  return {
    label: STRATEGY_LABELS[strategy] ?? prettify(strategy),
    connection: strategy,
    isEnterprise: false,
  };
}
